import { z } from 'zod';
import { DISTRIBUTE_MAX_LEADS } from '../constants';
import type { FunnelMetrics } from '../funnel/metrics';
import { WEIGHT_TOTAL, type RankingRow } from '../funnel/team';
import type { LeadListItem } from './leads';

export const AssignmentStrategy = { MANUAL: 'MANUAL', ROUND_ROBIN: 'ROUND_ROBIN' } as const;
export type AssignmentStrategy = (typeof AssignmentStrategy)[keyof typeof AssignmentStrategy];

export const ReassignTarget = { POOL: 'POOL', COORDINATOR: 'COORDINATOR' } as const;
export type ReassignTarget = (typeof ReassignTarget)[keyof typeof ReassignTarget];

export const assignmentRuleSchema = z
  .object({
    projectId: z.string().uuid(),
    strategy: z.nativeEnum(AssignmentStrategy),
    /** Asesores que entran en el reparto (orden = orden del round-robin). */
    advisorIds: z.array(z.string().uuid()).default([]),
    /** Pesos "de cada 10 leads" por asesor (suman 10); null = reparto parejo. */
    weights: z
      .record(z.string().uuid(), z.number().int().min(1).max(WEIGHT_TOTAL))
      .optional()
      .nullable(),
    /** Horas hábiles sin gestionar tras las cuales el lead se reasigna (null = desactivado). */
    autoReassignAfterHours: z.coerce.number().int().positive().max(720).optional().nullable(),
    reassignTo: z.nativeEnum(ReassignTarget).default(ReassignTarget.POOL),
    coordinatorId: z.string().uuid().optional().nullable(),
    /** El bot de atención escribe primero a los leads nuevos del proyecto (el asesor sigue siendo el responsable). */
    botFirst: z.boolean().default(false),
    active: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (!v.weights || v.strategy !== AssignmentStrategy.ROUND_ROBIN) return;
    const keys = Object.keys(v.weights);
    const ids = new Set(v.advisorIds);
    if (keys.length !== ids.size || keys.some((k) => !ids.has(k)))
      ctx.addIssue({
        code: 'custom',
        path: ['weights'],
        message: 'Cada asesor del turno necesita su parte de los 10 leads',
      });
    const sum = keys.reduce((s, k) => s + (v.weights?.[k] ?? 0), 0);
    if (sum !== WEIGHT_TOTAL)
      ctx.addIssue({
        code: 'custom',
        path: ['weights'],
        message: `Las partes deben sumar ${WEIGHT_TOTAL} (ahora suman ${sum})`,
      });
  });
export type AssignmentRuleInput = z.infer<typeof assignmentRuleSchema>;
export type AssignmentRulePayload = z.input<typeof assignmentRuleSchema>;

export interface AssignmentRuleDto extends AssignmentRuleInput {
  id: string;
  companyId: string;
  projectName: string;
  lastIndex: number;
  updatedAt: string;
}

export const distributeSchema = z
  .object({
    companyId: z.string().uuid(),
    /** Leads concretos, o bien todos los sin asignar del proyecto (los `DISTRIBUTE_MAX_LEADS` más antiguos). */
    leadIds: z.array(z.string().uuid()).max(DISTRIBUTE_MAX_LEADS).optional(),
    projectId: z.string().uuid().optional(),
    advisorIds: z.array(z.string().uuid()).min(1, 'Elige al menos un asesor'),
    reason: z.string().trim().max(300).optional().nullable(),
  })
  .refine((v) => v.leadIds?.length || v.projectId, {
    message: 'Indica leads o un proyecto',
    path: ['leadIds'],
  });
export type DistributeInput = z.infer<typeof distributeSchema>;
export type DistributePayload = z.input<typeof distributeSchema>;

/**
 * Resultado honesto de `POST /team/distribute` (Lote 6 · I4, P18): el reparto va por tandas de 100 (una transacción
 * cada una) sobre los `DISTRIBUTE_MAX_LEADS` leads más antiguos; `assigned` y `perAdvisor` cuentan lo que sí quedó
 * repartido aunque una tanda falle, `failed` trae cada lead que no se pudo con su motivo (ningún asesor elegido con
 * acceso a su proyecto, o la tanda se interrumpió) y `remaining` dice cuántos siguen en la bolsa al terminar.
 */
export interface DistributeResult {
  assigned: number;
  perAdvisor: Record<string, number>;
  failed: Array<{ id: string; reason: string }>;
  /** Cuántos siguen sin asesor al terminar; null si no se pudo contar (queda en el log; la web pide revisar la Bolsa). */
  remaining: number | null;
}

export interface PoolAdvisorRow {
  advisorId: string;
  advisorName: string;
  advisorFunction: string | null;
  /** Rol en la empresa (ASESOR / COORDINADOR / JEFE_COMERCIAL); null si ya no es miembro. */
  role: string | null;
  /** false = dado de baja (o ya no es miembro) pero aún tiene leads a su nombre. */
  active: boolean;
  total: number;
  activos: number;
  noGestionados: number;
  alDia: number;
  vencidosHoy: number;
  vencidosAyer: number;
  vencidos2a7: number;
  vencidosMas7: number;
  tareasVencidas: number;
}

/** Leads de la bolsa que la tarjeta de Equipo lista con su botón «Asignar» (la API no carga más que eso). */
export const POOL_LIST_LIMIT = 10;

export interface PoolResponse {
  companyId: string;
  projectId: string | null;
  advisors: PoolAdvisorRow[];
  unassigned: {
    total: number;
    noGestionados: number;
    /** Los `POOL_LIST_LIMIT` más antiguos sin asesor (lista corta de la tarjeta). */
    items: LeadListItem[];
    /** Captación del lead sin asesor más antiguo (null con la bolsa vacía). */
    oldestCapturedAt: string | null;
    /** Días calendario (zona de la empresa) que lleva esperando el más antiguo; 0 = llegó hoy. */
    oldestWaitingDays: number | null;
  };
  totals: Omit<PoolAdvisorRow, 'advisorId' | 'advisorName' | 'advisorFunction' | 'role' | 'active'>;
}

export interface RankingResponse {
  companyId: string;
  month: string;
  rows: RankingRow[];
  weights: Record<string, number>;
}

export interface DigitalTeamRow {
  advisorId: string;
  advisorName: string;
  metrics: FunnelMetrics;
  contactabilidad: number | null;
  recontacto: number | null;
}
